import * as v from "valibot"

import type { ArduinoTrigger } from "@/types/triggers"

/*
 * ==========================================
 * Backend Shapes
 * ==========================================
 *
 * Reference types reachable from `ArduinoTrigger`, used below via `satisfies` so a backend schema
 * change (a renamed/retyped field) surfaces as a type error here instead of silently drifting.
 */

export type BackendAction = NonNullable<ArduinoTrigger["actions"]>[number]
export type BackendDeviceSource = NonNullable<ArduinoTrigger["device_status_source"]>

type BackendRecipient = NonNullable<BackendAction["email"]>["delivery"]["to"][number]
type BackendLinkedDevice = NonNullable<BackendDeviceSource["linked_devices"]>[number]["device"]
type BackendProperty = NonNullable<ArduinoTrigger["linked_property"]>["property"]

/*
 * ==========================================
 * Draft Schemas
 * ==========================================
 */

/** Draft of a trigger action (email or push). `title` maps to the e-mail subject / push title. */
const ActionDraftSchema = v.object({
  title: v.string() satisfies v.GenericSchema<NonNullable<BackendAction["email"]>["subject"]["expression"]>,
  body: v.string() satisfies v.GenericSchema<NonNullable<BackendAction["email"]>["body"]["expression"]>,
  /** Recipient user ids. */
  to: v.array(v.string() satisfies v.GenericSchema<BackendRecipient["id"]>)
})
export type ActionDraft = v.InferOutput<typeof ActionDraftSchema>

/** Default grace period (seconds) applied to a device source when the user never picks one. */
export const DEFAULT_GRACE_PERIOD_SECONDS = 60

/**
 * Draft of a device-status source. `INCLUDE` fires for the listed devices; `EXCLUDE` fires for any
 * device except the listed ones (an empty list with `EXCLUDE` means "any device"). Grace periods
 * are in seconds and always set — the backend needs them to ever fire the trigger.
 */
const DeviceSourceDraftSchema = v.object({
  // The backend's `criteria` is a bare `string`; ours narrows it to the two values it ever sends.
  criteria: v.picklist(["INCLUDE", "EXCLUDE"]) satisfies v.GenericSchema<string>,
  deviceIds: v.array(v.string() satisfies v.GenericSchema<BackendLinkedDevice["id"]>),
  gracePeriodOnline: v.number() satisfies v.GenericSchema<NonNullable<BackendDeviceSource["grace_period_online"]>>,
  gracePeriodOffline: v.number() satisfies v.GenericSchema<NonNullable<BackendDeviceSource["grace_period_offline"]>>
})
export type DeviceSourceDraft = v.InferOutput<typeof DeviceSourceDraftSchema>

/** The "If" source: either a Cloud Variable (property) or a Device Status trigger. */
const TriggerSourceSchema = v.variant("kind", [
  v.object({ kind: v.literal("property"), propertyId: v.string() satisfies v.GenericSchema<BackendProperty["id"]> }),
  v.object({ kind: v.literal("device"), draft: DeviceSourceDraftSchema })
])
export type TriggerSource = v.InferOutput<typeof TriggerSourceSchema>

/**
 * The user-editable fields of a trigger draft. Unlike the schemas above, this one is never actually
 * parsed at runtime (a draft is valid mid-edit in states `SaveableTriggerDataSchema` would reject) —
 * it exists solely so its `satisfies v.GenericSchema<...>` checks keep `name`/`active` honest against
 * `ArduinoTrigger`, and so `TriggerBuilderData` stays derived from valibot like the rest.
 */
// eslint-disable-next-line unused-imports/no-unused-vars -- see comment above; used only for its inferred type
const TriggerBuilderDataSchema = v.object({
  name: v.string() satisfies v.GenericSchema<ArduinoTrigger["name"]>,
  active: v.boolean() satisfies v.GenericSchema<NonNullable<ArduinoTrigger["active"]>>,
  /** The "If" source. A trigger has at most one source at a time. */
  source: v.optional(TriggerSourceSchema),
  email: v.optional(ActionDraftSchema),
  push: v.optional(ActionDraftSchema)
})
export type TriggerBuilderData = v.InferOutput<typeof TriggerBuilderDataSchema>

/*
 * ==========================================
 * Save Validation
 * ==========================================
 *
 * These schemas don't change the shape of `TriggerBuilderData` (a draft is still valid mid-edit
 * with no source or an action with no recipients) — they're only used to check whether the current
 * draft is complete enough to save, via `isSaveableTriggerData`.
 */

/** An action ready to be saved always needs at least one recipient. */
const SaveableActionDraftSchema = v.pipe(
  ActionDraftSchema,
  v.check(draft => draft.to.length > 0, "At least one recipient is required")
)

/** A single-device source (`INCLUDE`) needs its device chosen; "any device" (`EXCLUDE`) may list none. */
const SaveableTriggerSourceSchema = v.pipe(
  TriggerSourceSchema,
  v.check(
    source => source.kind !== "device" || source.draft.criteria === "EXCLUDE" || source.draft.deviceIds.length > 0,
    "A device is required"
  )
)

/** A trigger can be saved once it has a source; actions are optional, but each present one must be complete. */
const SaveableTriggerDataSchema = v.object({
  name: v.string(),
  active: v.boolean(),
  source: SaveableTriggerSourceSchema,
  email: v.optional(SaveableActionDraftSchema),
  push: v.optional(SaveableActionDraftSchema)
})

export function isSaveableTriggerData(data: TriggerBuilderData): boolean {
  return v.safeParse(SaveableTriggerDataSchema, data).success
}
